<template>
  <FilterModal @reset="resetFilters" @apply="onFilterApply">
    <template #activator>
      <IconButton
        :icon-name="IconName.FILTER"
        label="Open filters"
        :variant="ButtonVariant.OUTLINED"
        :color="ButtonColor.TERTIARY"
      />
    </template>

    <template #body>
      <div class="text-left">
        <div class="py-4">
          <h3 class="mt-4 font-semibold text-secondary">
            {{ $t('filters-modal.type') }}
          </h3>
          <div class="flex flex-col">
            <Checkbox
              v-for="typeOption in typeOptions"
              :key="typeOption.key"
              class="mt-3"
              :model-value="filtersToSave.type.includes(typeOption.key)"
              :label="typeOption.label"
              @update:model-value="
                filtersToSave.type = getKeyToggledArray(
                  filtersToSave.type,
                  typeOption.key,
                )
              "
            />
          </div>
        </div>

        <h3 class="mt-7 font-semibold text-secondary">
          {{ $t('filters-modal.tags') }}
        </h3>
        <div class="flex flex-col">
          <Checkbox
            v-for="tag in tagsToShow"
            :key="tag.id"
            class="mt-3"
            :model-value="filtersToSave.tagIds.includes(tag.id)"
            :label="tag.name"
            @update:model-value="
              filtersToSave.tagIds = getKeyToggledArray(
                filtersToSave.tagIds,
                tag.id,
              )
            "
          />
        </div>
        <button
          v-if="!isEveryTagVisible && hiddenTagsCount > 0"
          type="button"
          class="text-primary font-bold text-sm mt-4 px-1 rounded-md"
          @click="isEveryTagVisible = true"
        >
          {{ `${$t('filters-modal.see-more')} (${hiddenTagsCount})` }}
        </button>

        <div v-if="organization?.isContentCustomizationEnabled">
          <h3 class="mt-7 font-semibold text-secondary">
            {{ $t('filters-modal.customizable-content') }}
          </h3>
          <Checkbox
            v-model="filtersToSave.customizableOnly"
            class="mt-3"
            :label="$t('filters-modal.show-customizable-content')"
          />
        </div>
        <div v-if="props.hub.isLaunchEnabled">
          <h3 class="mt-7 font-semibold text-secondary">
            {{ $t('filters-modal.status') }}
          </h3>
          <div class="flex flex-col">
            <Checkbox
              v-for="(statusOption, index) in LUNCH_METRICS_OPTIONS"
              :key="index"
              class="mt-3"
              :model-value="filtersToSave.status.includes(statusOption.key)"
              :label="statusOption.label"
              @update:model-value="
                filtersToSave.status = getKeyToggledArray(
                  filtersToSave.status,
                  statusOption.key,
                )
              "
            />
          </div>
        </div>
      </div>
    </template>
  </FilterModal>
</template>

<script setup lang="ts">
import {
  ButtonColor,
  ButtonVariant,
  FilterModal,
  IconButton,
  IconName,
} from '@exfluential/ui';
import { Checkbox, getKeyToggledArray } from '~/components/inputs/checkbox';
import { computed, ref } from '#imports';
import { ContentType, useContentTypeLabelMap } from '~/api/contents';
import { HubDto } from '~/api/hubs';
import { storeToRefs } from 'pinia';
import { useOrganizationStore } from '~/store/organization';
import { HubFilters } from '~/pages/_components/hub-details/_types';
import { USE_LUNCH_METRICS_OPTIONS } from '~/pages/_components/hub-details/_components/filters-modal/_constants';

type Props = {
  hub: HubDto;
};
const props = defineProps<Props>();

const LUNCH_METRICS_OPTIONS = USE_LUNCH_METRICS_OPTIONS();
const filters = defineModel<HubFilters>({ required: true });

const { organization } = storeToRefs(useOrganizationStore());

const isEveryTagVisible = ref(false);

const filtersToSave = ref({
  type: [...filters.value.type],
  tagIds: [...filters.value.tagIds],
  status: [...filters.value.status],
  customizableOnly: filters.value.customizableOnly,
});

watch(
  filters,
  () => {
    filtersToSave.value.type = [...filters.value.type];
    filtersToSave.value.tagIds = [...filters.value.tagIds];
    filtersToSave.value.status = [...filters.value.status];
    filtersToSave.value.customizableOnly = filters.value.customizableOnly;
  },
  { deep: true },
);

const resetFilters = () => {
  filtersToSave.value = {
    type: [],
    tagIds: [],
    status: [],
    customizableOnly: false,
  };
};

const typeOptions = computed(() => {
  const contentTypeLabelMap = useContentTypeLabelMap();

  let contentTypes = Object.values(ContentType);

  if (!organization.value?.isScormCourseEnabled) {
    contentTypes = contentTypes.filter(
      (contentType) => contentType !== ContentType.SCORM_COURSE,
    );
  }
  return contentTypes.map((type) => ({
    key: type,
    label: contentTypeLabelMap.value[type],
  }));
});

const tagsToShow = computed(() => {
  if (isEveryTagVisible.value) {
    return props.hub.tags;
  }
  return props.hub.tags.slice(0, 10);
});

const hiddenTagsCount = computed(() => {
  return props.hub.tags.length - tagsToShow.value.length;
});

const onFilterApply = () => {
  filters.value.type = [...filtersToSave.value.type];
  filters.value.tagIds = [...filtersToSave.value.tagIds];
  filters.value.status = [...filtersToSave.value.status];
  filters.value.customizableOnly = filtersToSave.value.customizableOnly;
};
</script>
